import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { ToolsIcon, KeysIcon, ViewsIcon, CommunityIcon } from '../icons/QuickActionIcons';

interface QuickActionItem {
    title: string;
    description: string;
    href: string;
    Icon: React.FC<{ className?: string }>;
    glowColor: string;
}

const quickActionItems: QuickActionItem[] = [
    {
        title: 'Tools',
        description: 'Explore our tools and utilities.',
        href: '/tools',
        Icon: ToolsIcon,
        glowColor: 'from-blue-500/30 to-purple-500/30',
    },
    {
        title: 'Share Free Keys',
        description: 'Share or get free keys instantly.',
        href: '/free-keys',
        Icon: KeysIcon,
        glowColor: 'from-orange-500/30 to-amber-500/30',
    },
    {
        title: 'Check Views',
        description: 'Check views analytics quickly.',
        href: '/check-view',
        Icon: ViewsIcon,
        glowColor: 'from-emerald-500/30 to-cyan-500/30',
    },
    {
        title: 'Community',
        description: 'Join the community and discussions.',
        href: '/community',
        Icon: CommunityIcon,
        glowColor: 'from-violet-500/30 to-pink-500/30',
    },
];

interface QuickActionCardProps {
    item: QuickActionItem;
}

const QuickActionCard: React.FC<QuickActionCardProps> = ({ item }) => {
    const { title, description, href, Icon, glowColor } = item;

    return (
        <Link
            to={href}
            className="group relative flex flex-col items-center p-6 rounded-2xl 
                 bg-black/40 backdrop-blur-sm border border-white/10
                 transition-all duration-300 ease-out
                 hover:bg-black/50 hover:border-orange-400/30 
                 hover:-translate-y-1 hover:shadow-lg hover:shadow-orange-500/10
                 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:ring-offset-2 focus:ring-offset-slate-900"
        >
            {/* Animated glow blob behind icon */}
            <div
                className={`absolute top-6 left-1/2 -translate-x-1/2 w-24 h-24 bg-gradient-to-br ${glowColor} 
                    rounded-full blur-2xl animate-glow-pulse`}
            />

            {/* Icon with floating animation */}
            <div className="relative z-10 w-16 h-16 mb-4 animate-icon-float group-hover:scale-110 group-hover:rotate-3 transition-transform">
                <Icon className="w-full h-full drop-shadow-lg" />
            </div>

            {/* Title */}
            <h3 className="relative z-10 text-lg font-semibold text-white mb-2 text-center group-hover:scale-105 transition-transform">
                {title}
            </h3>

            {/* Description */}
            <p className="relative z-10 text-sm text-white/60 text-center mb-4 leading-relaxed">
                {description}
            </p>

            {/* Action Button Pill with subtle glow */}
            <div className="relative overflow-hidden flex items-center justify-center gap-x-2 
                      bg-black/85 rounded-full group-hover:bg-black/75 
                      border border-white/10 group-hover:border-orange-400/30
                      transition-all duration-300 px-4 py-2 group-hover:scale-105 active:scale-95">
                {/* Shimmer sweep */}
                <div className="absolute inset-0 overflow-hidden rounded-full">
                    <div className="absolute inset-0 w-1/3 h-full animate-shimmer-sweep bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                </div>

                <span className="relative z-10 text-white/85 font-medium group-hover:text-white transition text-sm">
                    <span className="flex flex-row items-center gap-1">
                        Open
                        <ArrowUpRight className="w-3.5 h-3.5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                    </span>
                </span>
            </div>
        </Link>
    );
};

const QuickActionCards: React.FC = () => {
    return (
        <section className="w-full py-12 px-4">
            <div className="max-w-6xl mx-auto">
                {/* Grid: 4 cols on desktop, 2 on tablet, 1 on mobile */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {quickActionItems.map((item, index) => (
                        <QuickActionCard key={item.href} item={item} />
                    ))}
                </div>
            </div>
        </section>
    );
};

export default QuickActionCards;
